class ProductGallery487 extends HTMLElement {
  static observedAttributes = ['color', 'clientWidth'];

  constructor() {
    super();
    this.observeSelectedVariantAttribute();
  }

  connectedCallback() {
    var comp = this;
    this.elements = {
      prevButton: comp.querySelector('.prev-button') ?? null,
      nextButton: comp.querySelector('.next-button') ?? null,
      dotsContainer: comp.querySelector('.dots-container') ?? null,
    };

    this.options = {
      axis: 'x',
      align: 'start',
      container: `[data-track]`,
      slides: `[data-slide]`,
      loop: false,
    };

    this.option_thumbs = {
      axis: "y",
      align: 'center',
      containScroll: 'keepSnaps',
      dragFree: true,
    };

    this.viewportNodeMainCarousel = this.querySelector('.main-viewport');
    this.viewportNodeThumbCarousel = this.querySelector('.thumbnail-viewport');

    this.init();
    this.setupDots();
    this.setupVideos();
    this.handleInitialVariantSelection();
    this.handleResize();
    window.addEventListener("resize", this.handleResize);
  }

  // Videos render muted and looping with preload="none" and no autoplay attribute, so
  // nothing is fetched until a slide is actually on screen. Slides scrolled out of the
  // carousel are clipped by its overflow, and hidden variant galleries are display:none,
  // so both stop intersecting and get paused.
  setupVideos() {
    const videos = Array.from(this.querySelectorAll('video'));
    if (!videos.length) return;

    videos.forEach((video) => {
      // Set as properties too — the attributes alone aren't always enough for a scripted
      // play() to be allowed without a user gesture, notably on iOS.
      video.muted = true;
      video.playsInline = true;
      video.loop = true;
    });

    this.videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting) {
            const playing = target.play();
            if (playing) playing.catch(() => {});
          } else if (!target.paused) {
            target.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    videos.forEach((video) => this.videoObserver.observe(video));
  }

  observeSelectedVariantAttribute() {
    const closestElement = this.closest('[data-product-card-selected_variant_id]');

    if (!closestElement) {
      console.error("No ancestor element found with 'data-product-card-selected_variant_id' attribute.");
      return;
    }

    const observer = new MutationObserver(mutationsList => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-product-card-selected_variant_id') {
          const newValue = closestElement.getAttribute('data-product-card-selected_variant_id');
          if (newValue) {
            this.scrollToSlide(newValue);
          }
        }
      }
    });

    observer.observe(closestElement, { attributes: true });
  }

  handleInitialVariantSelection() {
    const closestElement = this.closest('[data-product-card-selected_variant_id]');
    if (!closestElement) {
      console.warn("No ancestor element found with 'data-product-card-selected_variant_id' attribute for initial variant selection.");
      return;
    }

    const initialVariantId = closestElement.getAttribute('data-product-card-selected_variant_id');
    if (initialVariantId) {
      this.scrollToSlide(initialVariantId);
    }
  }

  scrollToSlide(selectedVariantId) {
    const targetSlideIndex = this.findSlideIndexByVariantId(selectedVariantId);
    if (targetSlideIndex !== -1) {
      this.emblaApiMain.scrollTo(targetSlideIndex, false);
      this.emblaApiThumb.scrollTo(targetSlideIndex, false);
    }
  }

  findSlideIndexByVariantId(variantId) {
    const slides = this.emblaApiMain.slideNodes();
    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      const associatedVariantId = slide.dataset.targetVariant;
      if (associatedVariantId && associatedVariantId === variantId) {
        return i;
      }
    }
    return -1;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    console.log(`Attribute ${name} has changed.`);
  }

  destroy() {
    window.removeEventListener("resize", this.handleResize);
    this.videoObserver?.disconnect();
    this.emblaApiMain.destroy();
    this.emblaApiThumb.destroy();
  }

  init() {
    this.emblaApiMain = EmblaCarousel(
      this.viewportNodeMainCarousel,
      this.options,
      [
        EmblaCarouselWheelGestures({
          forceWheelAxis: 'x',
          skipSnaps: false,
        })
      ]
    );
    this.emblaApiThumb = EmblaCarousel(this.viewportNodeThumbCarousel, {
      ...this.options,
      ...this.option_thumbs,
    });

    const removeThumbBtnsClickHandlers = this.addThumbBtnsClickHandlers();
    const removeToggleThumbBtnsActive = this.addToggleThumbBtnsActive();
    const removePrevNextBtnsClickHandlers = this.addPrevNextBtnsClickHandlers(
      this.emblaApiMain,
      this.elements.prevButton,
      this.elements.nextButton
    );

    this.emblaApiMain.on('select', () => this.updateDots());

    this.emblaApiMain
      .on('destroy', removeThumbBtnsClickHandlers)
      .on('destroy', removeToggleThumbBtnsActive);

    this.emblaApiThumb
      .on('destroy', removeThumbBtnsClickHandlers)
      .on('destroy', removeToggleThumbBtnsActive);
  }

  addThumbBtnsClickHandlers() {
    const slidesThumbs = this.emblaApiThumb.slideNodes();

    const scrollToIndex = slidesThumbs.map((_, index) => () => {
      this.emblaApiMain.scrollTo(index, false);
    });

    slidesThumbs.forEach((slideNode, index) => {
      slideNode.addEventListener('click', scrollToIndex[index], false);
    });

    return () => {
      slidesThumbs.forEach((slideNode, index) => {
        slideNode.removeEventListener('click', scrollToIndex[index], false);
      });
    };
  }

  setupDots() {
    const dotsContainer = this.elements.dotsContainer;
    if (!dotsContainer) return;

    const slides = this.emblaApiMain.slideNodes();

    if (slides.length <= 1) {
      dotsContainer.classList.add("!hidden");
      return;
    }

    dotsContainer.innerHTML = ""; // Clear any existing dots

    slides.forEach((_, index) => {
      const dot = document.createElement("button");
      dot.className = "dot";
      dot.setAttribute("aria-label", `Go to slide ${index + 1}`);
      dot.addEventListener("click", () => this.emblaApiMain.scrollTo(index));
      dotsContainer.appendChild(dot);
    });

    this.updateDots();
  }

  updateDots() {
    const dotsContainer = this.elements.dotsContainer;
    if (!dotsContainer) return;

    const selectedIndex = this.emblaApiMain.selectedScrollSnap();
    dotsContainer.querySelectorAll(".dot").forEach((dot, index) => {
      dot.classList.toggle("active", index === selectedIndex);
    });
  }

  addToggleThumbBtnsActive() {
    const slidesThumbs = this.emblaApiThumb.slideNodes();

    const toggleThumbBtnsState = () => {
      const selected = this.emblaApiMain.selectedScrollSnap();
      const previous = this.emblaApiMain.previousScrollSnap();
      this.emblaApiThumb.scrollTo(selected);
      slidesThumbs[previous].classList.remove('selected');
      slidesThumbs[selected].classList.add('selected');
    };

    this.emblaApiMain.on('select', toggleThumbBtnsState);
    this.emblaApiThumb.on('init', toggleThumbBtnsState);

    return () => {
      const selected = this.emblaApiMain.selectedScrollSnap();
      slidesThumbs[selected].classList.remove('selected');
    };
  }

  addTogglePrevNextBtnsActive = (emblaApi, prevBtn, nextBtn) => {
    const togglePrevNextBtnsState = () => {
      if (emblaApi.canScrollPrev()) prevBtn.removeAttribute('disabled');
      else prevBtn.setAttribute('disabled', 'disabled');

      if (emblaApi.canScrollNext()) nextBtn.removeAttribute('disabled');
      else nextBtn.setAttribute('disabled', 'disabled');
    };

    emblaApi
      .on('select', togglePrevNextBtnsState)
      .on('init', togglePrevNextBtnsState)
      .on('reInit', togglePrevNextBtnsState);

    return () => {
      prevBtn.removeAttribute('disabled');
      nextBtn.removeAttribute('disabled');
    };
  };

  addPrevNextBtnsClickHandlers = (emblaApi, prevBtn, nextBtn) => {
    const scrollPrev = () => emblaApi.scrollPrev();
    const scrollNext = () => emblaApi.scrollNext();
    prevBtn.addEventListener('click', scrollPrev, false);
    nextBtn.addEventListener('click', scrollNext, false);

    const removeTogglePrevNextBtnsActive = this.addTogglePrevNextBtnsActive(
      emblaApi,
      prevBtn,
      nextBtn
    );

    return () => {
      removeTogglePrevNextBtnsActive();
      prevBtn.removeEventListener('click', scrollPrev, false);
      nextBtn.removeEventListener('click', scrollNext, false);
    };
  };

  handleResize = () => {
    const mainViewport = this.viewportNodeMainCarousel;
    if (!mainViewport) return;

    const width = window.innerWidth;

    if (this._lastWidth === width) return;
    this._lastWidth = width;

    const newAxis = width >= 768 ? "y" : "x";

    if (this._currentThumbAxis !== newAxis) {
      this._currentThumbAxis = newAxis;

      this.emblaApiThumb.reInit({
        ...this.options,
        ...this.option_thumbs,
        axis: newAxis,
      });
    }

    const height = mainViewport.offsetHeight;

    if (!height || height <= 0) return;

    document
      .querySelectorAll(".thumbnail-viewport")
      .forEach((thumbViewport) => {
        thumbViewport.style.setProperty(
          "--thumbnails-max-height-lg",
          `${height}px`
        );
      });
  };
}

customElements.define('product-gallery-487', ProductGallery487);